import { produce } from 'immer'
import { Signal } from '../utils/signals'
import {
  allFeatureFlags,
  featureFlagEnvKeys,
  featureFlags,
  FeatureFlags,
  FeatureFlagValues,
} from './index'
import { useSyncExternalStore } from 'react'

const LS_PREFIX = 'wistor_flags__'

class FeatureFlagsImpl implements FeatureFlags {
  public values = getAllFromLocalStorage()
  public readonly changeSignal = new Signal<FeatureFlagValues>()

  constructor() {
    // Define properties for dynamically updating flags in dev console. Usage:
    // featureFlags.singleLogin = true
    for (const flag of allFeatureFlags) {
      Object.defineProperty(this, flag, {
        get() { return this.values[flag] },
        set(value: FeatureFlagValues[keyof FeatureFlagValues]) {
          this.values = produce(this.values, (draft: FeatureFlagValues) => {
            draft[flag] = value
          })

          this.changeSignal.emit(this.values)
          setFlagToLocalStorage(flag, value)
        },
        enumerable: true
      })
    }
  }

  reset() {
    clearLocalStorage()
    this.values = getAllFromLocalStorage()
    this.changeSignal.emit(this.values)
  }
}

export function createFeatureFlags() {
  return new FeatureFlagsImpl()
}

function getAllFromLocalStorage(): FeatureFlagValues {
  const values: Partial<FeatureFlagValues> = {}
  for (const flag of allFeatureFlags) {
    const defaultValue = getEnvFlag(featureFlagEnvKeys[flag])
    values[flag] = getFlagFromLocalStorage(flag, defaultValue)
  }
  return values as FeatureFlagValues
}

function getFlagFromLocalStorage(flag: string, defaultValue: boolean): boolean {
  const key = LS_PREFIX + flag
  const item = localStorage.getItem(key)
  if (item == null) return defaultValue
  return parseBoolValue(item, defaultValue)
}

function setFlagToLocalStorage(flag: string, value: boolean) {
  const key = LS_PREFIX + flag
  localStorage.setItem(key, JSON.stringify(value))
}

function clearLocalStorage() {
  for (const flag of allFeatureFlags) {
    const key = LS_PREFIX + flag
    localStorage.removeItem(key)
  }
}

function getEnvFlag(name: string): boolean {
  return parseBoolValue(process.env[name])
}

function parseBoolValue(value: string | null | undefined, defaultValue = false) {
  try {
    return value != null ? Boolean(JSON.parse(value)) : defaultValue
  } catch (e) {
    console.warn('bad boolean var', e)
    return defaultValue
  }
}

export function useFeatureFlags() {
  return useSyncExternalStore(
    (callback) => {
      featureFlags.changeSignal.connect(callback)
      return () => featureFlags.changeSignal.disconnect(callback)
    },
    () => featureFlags.values
  )
}
